import type { DocumentExpiryDateDataReturn } from './useDocumentExpiryDate.ts';
import type { Dayjs } from 'dayjs';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'utils/queryKeys';
import { ContentType } from 'utils/getContentType';
import { apiDocumentServiceRepository } from 'services/repositories/implementations/ApiDocumentServiceRepository';
import { openNotification } from '../components';
import { useTranslation } from 'react-i18next';

interface UseDocumentExpiryDateProps {
  contentId: string;
  expiryDate: Dayjs | null;
}

const formatToIsoString = (date: Dayjs | null) => (date ? date.format('YYYY-MM-DD') : null);

export const useUpdateDocumentExpiryDate = ({ contentId, expiryDate }: UseDocumentExpiryDateProps) => {
  const { t } = useTranslation();
  const expiryDateIsoString = expiryDate ? formatToIsoString(expiryDate) : null;

  const queryClient = useQueryClient();

  const updateUseQueryLocalCache = (expiryDate: string | null) => {
    return queryClient.setQueryData<DocumentExpiryDateDataReturn>(QueryKeys.getDocumentExpiryAt(contentId), (oldData) => ({
      ...oldData,
      expire_at: expiryDate,
    }));
  };

  const updateDocumentExpiryDate = useMutation({
    mutationFn: async (newExpireAt: Dayjs | null) => {
      const newExpiryDateIsoString = formatToIsoString(newExpireAt);

      updateUseQueryLocalCache(newExpiryDateIsoString);

      await apiDocumentServiceRepository.patchContent({
        contentId,
        payload: {
          content: {
            expireAt: newExpiryDateIsoString,
          },
        },
      });
    },
    onSuccess: (_result, newExpireAt: Dayjs | null) => {
      const newExpiryDateIsoString = formatToIsoString(newExpireAt);
      const notificationTitle = newExpireAt
        ? t('share_document_v3.calendar_modal.link_expiry_updated', { expiryDate: newExpiryDateIsoString })
        : t('share_document_v3.calendar_modal.link_expiry_never');

      queryClient.invalidateQueries({ queryKey: QueryKeys.getDocumentMetadataByContentId(ContentType.DOCUMENT, contentId) });
      openNotification({
        type: 'success',
        title: notificationTitle,
      });
    },
    onError: () => {
      expiryDateIsoString && updateUseQueryLocalCache(expiryDateIsoString);
      openNotification({
        type: 'error',
        title: t('share_document_v3.calendar_modal.link_expiry_update_error'),
      });
    },
  });

  return {
    updateDocumentExpiryDate,
  };
};
